import { useEffect, type Dispatch, type SetStateAction } from 'react';
import type { DailyQuestAssignAndGetResponse } from '../../../models/apiTypes';
import type { useCreateUserAction } from '../../../hooks/useCreateUserAction';
import { getAllQuests } from '../../../utils/quest/questHelpers';
import posthog from 'posthog-js';

// Module-level constants — stable across renders; must be declared before the
// open-effect that reads them (react-hooks/immutability: no access-before-declare).
const X_QUEST_MODERATION_KEY_PREFIX = 'xf3_m_';
const X_QUEST_VERIFICATION_START_KEY_PREFIX = 'xf3_s_';
const X_QUEST_ERROR_TIMESTAMP_KEY_PREFIX = 'xf3_e_';
const X_URL_AI = 'https://x.com/y0news_ai';
const X_URL_CRYPTO = 'https://x.com/y0news_crypto';
const X_FOLLOW_QUEST_IDS = [208, 209] as const;

export interface UseXQuestFlowParams {
  isOpen: boolean;
  dailyQuestsData: DailyQuestAssignAndGetResponse | undefined;
  hourlyQuestsData: DailyQuestAssignAndGetResponse | undefined;
  marketingQuestsData: DailyQuestAssignAndGetResponse | undefined;
  setStartedVerificationQuestIds: Dispatch<SetStateAction<Set<number>>>;
  xQuestModerationTimestamps: Record<number, number>;
  setXQuestModerationTimestamps: Dispatch<SetStateAction<Record<number, number>>>;
  xQuestVerificationStartTimestamps: Record<number, number>;
  setXQuestVerificationStartTimestamps: Dispatch<SetStateAction<Record<number, number>>>;
  xQuestErrorTimestamps: Record<number, number>;
  setXQuestErrorTimestamps: Dispatch<SetStateAction<Record<number, number>>>;
  setActiveXModalQuestId: Dispatch<SetStateAction<number>>;
  setShowXVerificationErrorModal: Dispatch<SetStateAction<boolean>>;
  setShowModerationInfoModal: Dispatch<SetStateAction<boolean>>;
  createUserAction: ReturnType<typeof useCreateUserAction>['createUserAction'];
}

export interface UseXQuestFlowResult {
  setCloudStorageItem: (key: string, value: string, callback?: (success: boolean) => void) => void;
  isXFollowQuest: (questId: number) => boolean;
  getXUrlForQuest: (questId: number) => string;
  getXQuestModerationTimestamp: (questId: number) => number | null;
  clearXQuestModerationTimestamp: (questId: number) => void;
  getXQuestVerificationStartTimestamp: (questId: number) => number | null;
  clearXQuestVerificationStartTimestamp: (questId: number) => void;
  isXQuestOnModeration: (questId: number) => boolean;
  handleSubscribeToX: (questId: number) => void;
  handleVerifyXSubscription: (questId: number, userQuestId?: number) => Promise<void>;
}

export function useXQuestFlow({
  isOpen,
  dailyQuestsData,
  hourlyQuestsData,
  marketingQuestsData,
  setStartedVerificationQuestIds,
  xQuestModerationTimestamps,
  setXQuestModerationTimestamps,
  xQuestVerificationStartTimestamps,
  setXQuestVerificationStartTimestamps,
  xQuestErrorTimestamps,
  setXQuestErrorTimestamps,
  setActiveXModalQuestId,
  setShowXVerificationErrorModal,
  setShowModerationInfoModal,
  createUserAction,
}: UseXQuestFlowParams): UseXQuestFlowResult {
  // Функції для роботи з CloudStorage
  const getCloudStorageItem = (key: string, callback: (value: string | null) => void) => {
    try {
      const tg = window.Telegram?.WebApp;
      if (tg?.CloudStorage?.getItem) {
        tg.CloudStorage.getItem(key, (err: string | null, value: string | null) => {
          if (err) {
            console.warn(`[CloudStorage] Failed to get ${key}:`, err);
            callback(null);
          } else {
            callback(value);
          }
        });
      } else {
        callback(null);
      }
    } catch (e) {
      console.warn(`[CloudStorage] Error getting ${key}:`, e);
      callback(null);
    }
  };

  const setCloudStorageItem = (key: string, value: string, callback?: (success: boolean) => void) => {
    try {
      const tg = window.Telegram?.WebApp;
      if (tg?.CloudStorage?.setItem) {
        tg.CloudStorage.setItem(key, value, (err: string | null, isSaved: true | null) => {
          if (err) {
            console.warn(`[CloudStorage] Failed to set ${key}:`, err);
            callback?.(false);
          } else {
            callback?.(isSaved === true);
          }
        });
      } else {
        callback?.(false);
      }
    } catch (e) {
      console.warn(`[CloudStorage] Error setting ${key}:`, e);
      callback?.(false);
    }
  };

  const parseTimestamp = (value: string | null): number | null => {
    if (!value) return null;
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const removeTimestampFromState = (
    setState: Dispatch<SetStateAction<Record<number, number>>>,
    questId: number
  ) => {
    setState((prev) => {
      if (!(questId in prev)) return prev;
      const next = { ...prev };
      delete next[questId];
      return next;
    });
  };

  const loadXQuestTimestampFromStorage = (
    questId: number,
    keyPrefix: string,
    setState: Dispatch<SetStateAction<Record<number, number>>>,
    onLoaded?: (timestamp: number) => void
  ) => {
    const key = keyPrefix + questId;

    getCloudStorageItem(key, (value) => {
      const timestamp = parseTimestamp(value);
      if (timestamp !== null) {
        setState((prev) => ({ ...prev, [questId]: timestamp }));
        onLoaded?.(timestamp);
      }
    });
  };

  // Завантажуємо стан кнопок з CloudStorage при відкритті модалки
  useEffect(() => {
    if (isOpen) {
      posthog.capture('daily_quests_opened');
      for (const qid of X_FOLLOW_QUEST_IDS) {
        loadXQuestTimestampFromStorage(
          qid,
          X_QUEST_MODERATION_KEY_PREFIX,
          setXQuestModerationTimestamps
        );
        loadXQuestTimestampFromStorage(
          qid,
          X_QUEST_VERIFICATION_START_KEY_PREFIX,
          setXQuestVerificationStartTimestamps
        );
        loadXQuestTimestampFromStorage(
          qid,
          X_QUEST_ERROR_TIMESTAMP_KEY_PREFIX,
          setXQuestErrorTimestamps
        );
      }

      // Завантажуємо стан кнопок для квестів 6, 7, 108 з CloudStorage
      const questIds = [6, 7, 108];
      questIds.forEach((questId) => {
        const key = `quest_${questId}_started`;
        getCloudStorageItem(key, (value) => {
          if (value === 'true') {
            // Знаходимо userQuestId для цього questId
            const allQuests = getAllQuests(
              dailyQuestsData?.quests,
              hourlyQuestsData?.quests,
              marketingQuestsData?.quests
            );
            const quest = allQuests.find((q) => q.quest_id === questId);
            if (quest?.user_quest_id) {
              setStartedVerificationQuestIds((prev) => new Set(prev).add(quest.user_quest_id!));
            }
          }
        });
      });
    }
    // Тригер: open + quest lists. set* / module constants stable; load helper is
    // recreated each render — adding it would re-hit CloudStorage every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load-on-open only
  }, [isOpen, dailyQuestsData, hourlyQuestsData, marketingQuestsData]);

  const isXFollowQuest = (questId: number): boolean =>
    (X_FOLLOW_QUEST_IDS as readonly number[]).includes(questId);

  const getXUrlForQuest = (questId: number): string => {
    if (questId === 208) return X_URL_AI;
    return X_URL_CRYPTO;
  };

  const getActionForXQuest = (questId: number): "follow_x_y0news_ai" | "follow_x_y0news_crypto" => {
    if (questId === 208) return "follow_x_y0news_ai";
    return "follow_x_y0news_crypto";
  };

  // Функції для роботи з Telegram CloudStorage для квестів підписки на X (per-quest)
  const getXQuestModerationTimestamp = (questId: number): number | null => {
    return xQuestModerationTimestamps[questId] ?? null;
  };

  const setXQuestModerationTimestamp = (questId: number, timestamp: number): void => {
    setXQuestModerationTimestamps((prev) => ({ ...prev, [questId]: timestamp }));
    setCloudStorageItem(X_QUEST_MODERATION_KEY_PREFIX + questId, timestamp.toString());
  };

  const clearXQuestModerationTimestamp = (questId: number): void => {
    removeTimestampFromState(setXQuestModerationTimestamps, questId);
    setCloudStorageItem(X_QUEST_MODERATION_KEY_PREFIX + questId, "");
  };

  const getXQuestVerificationStartTimestamp = (questId: number): number | null => {
    return xQuestVerificationStartTimestamps[questId] ?? null;
  };

  const setXQuestVerificationStartTimestamp = (questId: number, timestamp: number): void => {
    setXQuestVerificationStartTimestamps((prev) => ({ ...prev, [questId]: timestamp }));
    setCloudStorageItem(X_QUEST_VERIFICATION_START_KEY_PREFIX + questId, timestamp.toString());
  };

  const clearXQuestVerificationStartTimestamp = (questId: number): void => {
    removeTimestampFromState(setXQuestVerificationStartTimestamps, questId);
    setCloudStorageItem(X_QUEST_VERIFICATION_START_KEY_PREFIX + questId, "");
  };

  const getXQuestErrorTimestamp = (questId: number): number | null => {
    return xQuestErrorTimestamps[questId] ?? null;
  };

  const setXQuestErrorTimestamp = (questId: number, timestamp: number): void => {
    setXQuestErrorTimestamps((prev) => ({ ...prev, [questId]: timestamp }));
    setCloudStorageItem(X_QUEST_ERROR_TIMESTAMP_KEY_PREFIX + questId, timestamp.toString());
  };

  const clearXQuestErrorTimestamp = (questId: number): void => {
    removeTimestampFromState(setXQuestErrorTimestamps, questId);
    setCloudStorageItem(X_QUEST_ERROR_TIMESTAMP_KEY_PREFIX + questId, "");
  };

  const isXQuestOnModeration = (questId: number): boolean => {
    const timestamp = getXQuestModerationTimestamp(questId);
    if (!timestamp) return false;
    const now = Date.now();
    const elapsed = now - timestamp;
    const ONE_HOUR_MS = 60 * 60 * 1000;
    return elapsed < ONE_HOUR_MS;
  };

  // Обробка підписки на X (перша дія - відкриває твітер)
  const handleSubscribeToX = (questId: number) => {
    window.open(getXUrlForQuest(questId), '_blank');
    // Встановлюємо час початку перевірки, щоб кнопка змінилась на "ПЕРЕВІРИТИ ПІДПИСКУ"
    // (якщо ще не встановлено timestamp модерації)
    if (!getXQuestModerationTimestamp(questId)) {
      const startTime = Date.now();
      setXQuestVerificationStartTimestamp(questId, startTime);
    }
  };

  // Обробка перевірки підписки на X
  const handleVerifyXSubscription = async (questId: number, userQuestId?: number) => {
    if (!userQuestId) return;

    const now = Date.now();
    const existingTimestamp = getXQuestModerationTimestamp(questId);
    const errorTimestamp = getXQuestErrorTimestamp(questId);
    const verificationStartTimestamp = getXQuestVerificationStartTimestamp(questId);

    // Якщо квест вже на модерації - нічого не робимо
    if (existingTimestamp) {
      return;
    }

    // Запам'ятовуємо активний квест для модалок
    setActiveXModalQuestId(questId);

    // Якщо немає timestamp помилки - це перша перевірка
    // ЗАВЖДИ показуємо помилку при першому натиску на "почати перевірку"
    if (!errorTimestamp) {
      // Встановлюємо час початку перевірки (якщо ще не встановлено)
      if (!verificationStartTimestamp) {
        const startTime = now;
        setXQuestVerificationStartTimestamp(questId, startTime);
      }

      // Показуємо помилку одразу (без затримки)
        setShowXVerificationErrorModal(true);
      // Встановлюємо timestamp помилки
        const errorTimestampNow = Date.now();
        setXQuestErrorTimestamp(questId, errorTimestampNow);

      return;
    }

    // Якщо є timestamp помилки - це другий раз, одразу переходимо до модерації
    // Оптимістично записуємо timestamp і показуємо модалку одразу
    setXQuestModerationTimestamp(questId, now);

    // Очищаємо час початку перевірки та timestamp помилки
    clearXQuestVerificationStartTimestamp(questId);
    clearXQuestErrorTimestamp(questId);

    // Показуємо вікно про модерацію одразу
    setShowModerationInfoModal(true);

    // Відправляємо user action в фоні (не блокуємо UI)
    createUserAction(getActionForXQuest(questId)).catch((error: unknown) => {
      console.error(`Failed to create ${getActionForXQuest(questId)} action:`, error);
      // У разі помилки відкочуємо timestamp і показуємо помилку
      clearXQuestModerationTimestamp(questId);
      setShowModerationInfoModal(false);
      setShowXVerificationErrorModal(true);
    });
  };

  return {
    setCloudStorageItem,
    isXFollowQuest,
    getXUrlForQuest,
    getXQuestModerationTimestamp,
    clearXQuestModerationTimestamp,
    getXQuestVerificationStartTimestamp,
    clearXQuestVerificationStartTimestamp,
    isXQuestOnModeration,
    handleSubscribeToX,
    handleVerifyXSubscription,
  };
}
